// Copyright 2015-2023 Nstream, inc.
// All rights reserved.

import type {Observes} from "@swim/util";
import type {Mapping} from "@swim/util";
import type {Value} from "@swim/structure";
import {DateTime} from "@swim/time";
import {TimeZone} from "@swim/time";
import {ValueDownlink} from "@swim/client";
import type {Trait} from "@swim/model";
import {Look} from "@swim/theme";
import {Feel} from "@swim/theme";
import type {TextRunView} from "@swim/graphics";
import {TraitViewRef} from "@swim/controller";
import type {PanelView} from "@swim/panel";
import type {PanelController} from "@swim/panel";
import {DataPointView} from "@swim/chart";
import {Status} from "@nstream/domain";
import {TimeGaugeController} from "@nstream/widget";

/** @public */
export class PulseMemoryController extends TimeGaugeController {
  @TraitViewRef({
    extends: true,
    initView(panelView: PanelView): void {
      super.initView(panelView);
      panelView.style.setIntrinsic({
        display: "flex",
        alignItems: "center",
      });
      this.owner.dial.insertView().set({
        limit: 100,
      });
    },
    viewDidMount(panelView: PanelView): void {
      this.owner.consume(panelView);
      const titleView = this.owner.gaugeTitle.attachView() as TextRunView;
      const font = panelView.getLook(Look.font);
      if (font !== void 0) {
        titleView.font.set(font.withSize(32));
      }
    },
    viewWillUnmount(panelView: PanelView): void {
      this.owner.unconsume(panelView);
    },
    createView(): PanelView {
      return (super.createView() as PanelView).setIntrinsic({
        panelStyle: "card",
        headerTitle: "Memory",
      });
    },
  })
  override readonly panel!: TraitViewRef<this, Trait, PanelView> & PanelController["panel"]  & TimeGaugeController["panel"] & Observes<PanelView>;

  protected override formatPanelSubtitle(dataPointView: DataPointView<DateTime, number>): string | undefined {
    return void 0;
  }

  @ValueDownlink({
    laneUri: "pulse",
    consumed: true,
    didSet(value: Value): void {
      //console.log("PulseMemoryController.pulseDownlink.didSet:", value.toLike());
      const system = value.get("system");
      const memUsage = system.get("memUsage").numberValue(0);
      const memTotal = system.get("memTotal").numberValue(100);
      const memPercentage = (memUsage * 100) / memTotal;
      const timestamp = value.get("timestamp").numberValue(void 0);
      const t = timestamp !== void 0 ? new DateTime(timestamp, TimeZone.local()) : DateTime.current(TimeZone.local());

      const latestView = new DataPointView<DateTime, number>().set({
        x: t,
        y: memPercentage,
        color: Look.accentColor,
      });
      const status = PulseMemoryController.memUsageStatus(memPercentage);
      latestView.modifyMood(Feel.default, status.moodModifier);
      this.owner.latest.insertView(null, latestView);
    },
  })
  readonly pulseDownlink!: ValueDownlink<this>;

  static readonly memUsageStatus: Mapping<number, Status> = Status.worsening(0, 0.3, 0.6, 0.9, 1);
}
